import { useEffect, useState } from "react";
import EmptyState from "@/components/ui/empty-state";
import { useAuth, useUser } from "@clerk/react";
import { toast } from "sonner";
import { ListPlusIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { useOrganizationSettingsStore } from "@/stores";
import type { SavedCustomField } from "@/types/types";
import AddCustomFieldDialog from "@/components/dashboard_create_campaign/AddCustomFieldDialog";

const TYPE_LABEL: Record<SavedCustomField["type"], string> = {
  string: "Text",
  number: "Number",
  boolean: "Yes/No",
  date: "Date",
};

/**
 * Org-wide reusable custom CRM fields (organizations.custom_field_library).
 * Entries land here automatically whenever a campaign is created with a
 * custom field (latest definition wins per name) — this section is the
 * management surface: rename/retype/redescribe or delete. Editing here
 * never touches fields already copied into campaigns.
 */
export function SavedFieldsSection() {
  const { organizationInfo, saveOrganizationSetting, fetchOrganizationInfo, isLoading } =
    useOrganizationSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();

  const [fields, setFields] = useState<SavedCustomField[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Always refetch on mount — the cached organizationInfo may predate the
  // library key (or another tab's campaign create just added entries).
  useEffect(() => {
    if (!orgId || !user?.id) return;
    void (async () => {
      const token = await getToken();
      if (token) await fetchOrganizationInfo(orgId, user.id, token);
    })();
  }, [orgId, user?.id, getToken, fetchOrganizationInfo]);

  useEffect(() => {
    setFields((organizationInfo?.custom_field_library as SavedCustomField[] | undefined) ?? []);
  }, [organizationInfo]);

  const persist = async (next: SavedCustomField[], successMsg: string) => {
    if (!orgId || !user?.id) return;
    setSaving(true);
    const prev = fields;
    setFields(next);
    try {
      const token = await getToken();
      if (!token) throw new Error("no token");
      const ok = await saveOrganizationSetting(orgId, user.id, "custom_field_library", next, token);
      if (!ok) throw new Error("save failed");
      toast.success(successMsg);
    } catch {
      setFields(prev);
      toast.error("Failed to save the field library");
    } finally {
      setSaving(false);
    }
  };

  const editingField = editingIndex !== null ? fields[editingIndex] : null;

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Saved Fields</FrameTitle>
        <FrameDescription>
          Custom fields from your campaigns, ready to quick-add in the campaign wizard.
        </FrameDescription>
      </FrameHeader>
      {isLoading && fields.length === 0 && (
        <FramePanel className="p-3">
          <Skeleton className="h-16 w-full rounded-lg" />
        </FramePanel>
      )}
      {!isLoading && fields.length === 0 && (
        <FramePanel>
          <EmptyState
            className="mb-10"
            icon={<ListPlusIcon />}
            text="No saved fields yet"
          />
        </FramePanel>
      )}
      {fields.length > 0 && (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              <TableHead className="w-40">Type</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((f, i) => (
              <TableRow key={`${f.name}-${i}`} data-context-menu>
                {/* max-w-0: the column takes what's left, so the description truncates. */}
                <TableCell className="max-w-0">
                  <div className="grid min-w-0 leading-tight">
                    <span className="truncate text-sm font-medium">{f.name}</span>
                    {f.description && (
                      <span className="truncate text-xs text-muted-foreground">{f.description}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="ghost">{TYPE_LABEL[f.type] ?? f.type}</Badge>
                    {f.type === "number" && f.default_value !== undefined && (
                      <span className="font-mono text-xs text-muted-foreground pr-2">
                        default {f.default_value}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="muted-ghost"
                          size="icon-sm"
                          aria-label="Field options"
                          disabled={saving}
                        >
                          <MoreVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setEditingIndex(i)}>
                          <PencilIcon />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() =>
                            void persist(
                              fields.filter((_, idx) => idx !== i),
                              `Deleted "${f.name}" from saved fields`
                            )
                          }
                        >
                          <Trash2Icon />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddCustomFieldDialog
        editMode
        externalOpen={editingIndex !== null}
        onOpenChange={(open) => {
          if (!open) setEditingIndex(null);
        }}
        editInitialValues={
          editingField
            ? {
                name: editingField.name,
                description: editingField.description,
                type: editingField.type,
                defaultValue: editingField.default_value,
              }
            : undefined
        }
        onEdit={(name, description, type, defaultValue) => {
          if (editingIndex === null) return;
          const next = fields.map((f, idx) =>
            idx === editingIndex
              ? {
                  name,
                  description,
                  type,
                  ...(type === "number" && defaultValue !== undefined
                    ? { default_value: defaultValue }
                    : {}),
                }
              : f
          );
          setEditingIndex(null);
          void persist(next, `Updated "${name}"`);
        }}
        onAdd={() => {}}
        existingFieldNames={fields
          .filter((_, idx) => idx !== editingIndex)
          .map((f) => f.name)}
        disabled={saving}
      />
    </Frame>
  );
}
